/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

import { TMeasurabilityProjectQueryParams } from '../types';
import { TParams } from '@global-types/index';
import { TMesurabilityProject } from '@pages/Offsets/types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IMeasurabilityProjectSlice {
	/* Pagination */
	measurabilityProjectsTotalPages: number;
	measurabilityProjectsPage: number;
	measurabilityProjectsTotal: number;
	measurabilityProjectsPageSize: number;
	measurabilityProjectsQueryParams: TParams;
	addMeasurabilityProjectFormOpened: boolean;
	updateMeasurabilityProjectFormOpened: boolean;
	technicalDocuments: string[];

	idsLogoToDelete: string[];
	idsCoverUrlToDelete: string[];
	idsPublicTechnicalDocumentsToDelete: string[];
	idsInternalTechnicalDocumentsToDelete: string[];

	initialMeasurabilityData: TMesurabilityProject | null;

	setInitialMeasurabilityData: (
		_initialMeasurabilityData: TMesurabilityProject
	) => void;

	setIdsLogoToDelete: (
		_idsLogoToDelete: string[]
	) => void;

	setIdsCoverUrlToDelete: (
		_idsCoverUrlToDelete: string[]
	) => void;

	setIdsPublicTechnicalDocumentsToDelete: (
		_idsPublicTechnicalDocumentsToDelete: string[]
	) => void;

	setIdsInternalTechnicalDocumentsToDelete: (
		_idsPublicTechnicalDocumentsToDelete: string[]
	) => void;

	setPublicTechnicalDocuments: (
		_technicalDocuments: string[]
	) => void;

	setAddMeasurabilityProjectFormOpened: (
		_addMeasurabilityProjectFormOpened: boolean
	) => void;

	setUpdateMeasurabilityProjectFormOpened: (
		_updateMeasurabilityProjectFormOpened: boolean
	) => void;

	/* Datatable */
	setMeasurabilityProjectsPage: (
		_measurabilityProjectsPage: number
	) => void;

	setMeasurabilityProjectsQueryParams: (
		_measurabilityProjectsQueryParams: TMeasurabilityProjectQueryParams
	) => void;

	setMeasurabilityProjectsPageSize: (
		_pageSize: number
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	measurabilityProjectsTotal: 0,
	measurabilityProjectsPage: 1,
	measurabilityProjectsPageSize: 50,
	measurabilityProjectsTotalPages: 0,
	addMeasurabilityProjectFormOpened: false,
	updateMeasurabilityProjectFormOpened: false,
	/**************/
	measurabilityProjects: [],
	measurabilityProjectsQueryParams: {},
	technicalDocuments: [],
	idsLogoToDelete: [],
	idsCoverUrlToDelete: [],
	idsPublicTechnicalDocumentsToDelete: [],
	idsInternalTechnicalDocumentsToDelete: [],
	initialMeasurabilityData: null,
};

export const createMeasurabilityProjectsSlice: StateCreator<
	TStore,
	[],
	[],
	IMeasurabilityProjectSlice
> = (set) => ({
	...initialState,

	setInitialMeasurabilityData: (
		_initialMeasurabilityData: TMesurabilityProject
	) =>
		set({
			initialMeasurabilityData: _initialMeasurabilityData,
		}),

	setIdsLogoToDelete: (_idsLogoToDelete: string[]) =>
		set({
			idsLogoToDelete: _idsLogoToDelete,
		}),
	setIdsCoverUrlToDelete: (
		_idsCoverUrlToDelete: string[]
	) =>
		set({
			idsCoverUrlToDelete: _idsCoverUrlToDelete,
		}),
	setIdsPublicTechnicalDocumentsToDelete: (
		_idsPublicTechnicalDocumentsToDelete: string[]
	) =>
		set({
			idsPublicTechnicalDocumentsToDelete:
				_idsPublicTechnicalDocumentsToDelete,
		}),

	setIdsInternalTechnicalDocumentsToDelete: (
		_idsInternalTechnicalDocumentsToDelete: string[]
	) =>
		set({
			idsInternalTechnicalDocumentsToDelete:
				_idsInternalTechnicalDocumentsToDelete,
		}),

	setPublicTechnicalDocuments: (
		_technicalDocuments: string[]
	) =>
		set({
			technicalDocuments: _technicalDocuments,
		}),
	setAddMeasurabilityProjectFormOpened: (
		_addMeasurabilityProjectFormOpened: boolean
	) =>
		set({
			addMeasurabilityProjectFormOpened:
				_addMeasurabilityProjectFormOpened,
		}),

	setUpdateMeasurabilityProjectFormOpened: (
		_updateMeasurabilityProjectFormOpened: boolean
	) =>
		set({
			updateMeasurabilityProjectFormOpened:
				_updateMeasurabilityProjectFormOpened,
		}),
	setMeasurabilityProjectsQueryParams: (
		_queryParams: TMeasurabilityProjectQueryParams
	) =>
		set({
			measurabilityProjectsQueryParams: _queryParams,
		}),
	setMeasurabilityProjectsTotal: (_total: number) =>
		set({ measurabilityProjectsTotal: _total }),
	setMeasurabilityProjectsPage: (_page: number) =>
		set({ measurabilityProjectsPage: _page }),
	setMeasurabilityProjectsPageSize: (_pageSize: number) =>
		set({ measurabilityProjectsPageSize: _pageSize }),
	setMeasurabilityProjectsTotalPages: (
		_totalPages: number
	) =>
		set({ measurabilityProjectsTotalPages: _totalPages }),
});
